/**
 * Widget IIFE 入口。
 *
 * 挂载策略：
 *   1. 立即渲染右下角气泡按钮（原生 DOM，尺寸最小）
 *   2. 点击后动态挂 shadow root，Preact 渲染面板（隔离宿主页样式）
 *
 * 宿主页面：
 *   <script async src="https://chat.toriri.io/widget.js" data-product="prod_xxx"></script>
 */

import { h, render } from 'preact';
import { Panel } from './panel/Panel';
import { widgetCss } from './panel/styles';
import { detectHost } from './lib/api';
import { createNotifyAudio } from './lib/notify';

const CURRENT_SCRIPT: HTMLScriptElement | null =
  (document.currentScript as HTMLScriptElement | null) ??
  (document.querySelector('script[src*="widget.js"]') as HTMLScriptElement | null);

const PRODUCT = CURRENT_SCRIPT?.dataset.product ?? 'demo'; // 产品 slug，不是内部 ID
const HOST = detectHost(CURRENT_SCRIPT);
const bubbleAudio = createNotifyAudio(HOST); // 只用来提前 unlock 一次

let root: HTMLDivElement | null = null;
let shadow: ShadowRoot | null = null;
let bubble: HTMLButtonElement | null = null;
let open = false;

function ensureRoot(): ShadowRoot {
  if (shadow) return shadow;

  root = document.createElement('div');
  root.id = 'chat-toriri-widget-root';
  root.setAttribute('data-chat-toriri', 'widget');
  // 挂载点默认零尺寸浮层，widget 本身通过 fixed 定位。
  // 不使用 !important，宿主页可以覆盖挂载点样式（例如 display:none 隐藏 widget）。
  root.style.cssText = 'position:fixed;z-index:2147483000;top:0;left:0;width:0;height:0;';
  document.body.appendChild(root);

  shadow = root.attachShadow({ mode: 'closed' });

  const style = document.createElement('style');
  style.textContent = widgetCss;
  shadow.appendChild(style);

  return shadow;
}

function renderBubble() {
  const sr = ensureRoot();

  bubble = document.createElement('button');
  bubble.className = 'ct-bubble';
  bubble.setAttribute('aria-label', '打开客服对话');
  bubble.innerHTML = `<svg viewBox="0 0 24 24" fill="none">
    <path d="M12 3C7.03 3 3 6.58 3 11c0 2.4 1.19 4.55 3.06 6.02L5 21l4.15-2.02c.9.2 1.86.32 2.85.32 4.97 0 9-3.58 9-8.3S16.97 3 12 3z" fill="currentColor"/>
  </svg>`;
  bubble.addEventListener('click', () => {
    // 用户点气泡是首次可靠的 activation，趁机解锁音频权限
    void bubbleAudio.unlock();
    open ? closePanel() : openPanel();
  });
  sr.appendChild(bubble);
}

let panelHost: HTMLDivElement | null = null;

function openPanel() {
  if (open) return;
  const sr = ensureRoot();
  panelHost = document.createElement('div');
  panelHost.className = 'ct-root';
  sr.appendChild(panelHost);

  render(
    h(Panel, {
      host: HOST,
      productSlug: PRODUCT,
      onClose: closePanel,
    }),
    panelHost,
  );

  if (bubble) bubble.style.display = 'none';
  open = true;
}

function closePanel() {
  if (!open) return;
  if (panelHost) {
    render(null, panelHost);
    panelHost.remove();
    panelHost = null;
  }
  if (bubble) bubble.style.display = '';
  open = false;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderBubble);
} else {
  renderBubble();
}
